/**
 * Merge overlapping or touching `[start, end)` intervals into the
 * smallest equivalent non-overlapping list.
 *
 *   Input:  [ 9–11, 10–12, 13–14 ]
 *   Output: [ 9–12,          13–14 ]
 *
 * Intervals that just touch (`end === start`) are also merged, so
 * `9–10` + `10–11` becomes `9–11`. Invalid intervals (end ≤ start,
 * bad ISO strings) are dropped silently.
 *
 * The algorithm: sort by start time, walk left-to-right, extend the
 * last-kept interval if the current one overlaps, otherwise push a
 * new one.
 */
import moment from 'moment-timezone';
import type { Interval } from './types';

export function mergeIntervals(
  intervals: Interval[],
  timezone: string
): Interval[] {
  if (intervals.length <= 1) {
    return [...intervals];
  }

  const parsed = intervals
    .map((iv) => ({
      start: moment.tz(iv.start, timezone),
      end: moment.tz(iv.end, timezone),
    }))
    .filter(
      ({ start, end }) =>
        start.isValid() && end.isValid() && start.isBefore(end)
    )
    .sort((a, b) => a.start.valueOf() - b.start.valueOf());

  if (parsed.length === 0) {
    return [];
  }

  const merged = [parsed[0]];
  for (let i = 1; i < parsed.length; i += 1) {
    const current = parsed[i];
    const last = merged[merged.length - 1];
    if (current.start.isSameOrBefore(last.end)) {
      if (current.end.isAfter(last.end)) {
        last.end = current.end;
      }
    } else {
      merged.push(current);
    }
  }

  return merged.map(({ start, end }) => ({
    start: start.toISOString(),
    end: end.toISOString(),
  }));
}
